/* =============================================================
   NAPUPLANT — JavaScript global (vanilla, sin dependencias)
   Extraído del diseño aprobado en Diseño/*.dc.html.

   Comportamientos:
     1. Reveal on scroll  — IntersectionObserver, .reveal/.reveal-stagger -> .in
     2. Nav shrink        — clase .shrink en header.nav al superar 40px de scroll
     3. Carrusel          — scroll horizontal con snap + botones prev/next
     4. Parallax          — [data-parallax-abs] / [data-parallax-rel]

   Convenciones de marcado (data-attributes, section-editor friendly):
     - Reveal:    class="reveal"  /  class="reveal-stagger"
     - Nav:       <header class="nav" data-napu-nav>  (o id="napu-nav")
     - Carrusel:  contenedor scroll  -> [data-napu-carousel]
                  botón anterior     -> [data-napu-carousel-prev]
                  botón siguiente    -> [data-napu-carousel-next]
                  (controles y carrusel dentro de [data-napu-carousel-scope])
     - Parallax:  [data-parallax-abs="0.3"]  /  [data-parallax-rel="0.1"]
   ============================================================= */

(function () {
  'use strict';

  /* ---------- 1. Reveal on scroll ---------- */
  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    if (!('IntersectionObserver' in window)) return null;
    // Umbral 0.12 para que una sección aparezca cuando se ve un trozo, no un
    // píxel. PERO un elemento más alto que la pantalla nunca llega al 12 % de
    // sí mismo visible (el cuerpo de una página legal mide varias pantallas),
    // así que se quedaba invisible hasta el primer scroll. Para esos, con que
    // asome vale: se observa también el umbral 0 y se decide por altura.
    revealObserver = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var alto = entry.boundingClientRect.height;
        var esAlto = alto > window.innerHeight * 0.6;
        if (entry.intersectionRatio >= 0.12 || esAlto) {
          entry.target.classList.add('in');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: [0, 0.12] });
    return revealObserver;
  }

  function observeReveals(root) {
    var scope = root || document;
    var obs = getRevealObserver();
    var els = scope.querySelectorAll('.reveal, .reveal-stagger');
    if (!obs) {
      // Sin soporte: mostrar todo directamente.
      els.forEach(function (el) { el.classList.add('in'); });
      return;
    }
    els.forEach(function (el) {
      if (el.dataset.napuObserved) return;
      el.dataset.napuObserved = '1';
      obs.observe(el);
    });
  }

  /* ---------- 2. Nav shrink al hacer scroll ---------- */
  function scrollY() {
    return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
  }

  function initNavShrink() {
    var navs = document.querySelectorAll('header.nav, [data-napu-nav], #napu-nav');
    if (!navs.length) return;
    function onScroll() {
      var shrink = scrollY() > 40;
      navs.forEach(function (nav) { nav.classList.toggle('shrink', shrink); });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- 3. Carrusel horizontal con snap ---------- */
  function carouselScrollAmount(carousel) {
    var first = carousel.querySelector(':scope > *');
    if (first) {
      var styles = window.getComputedStyle(carousel);
      var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      var step = first.getBoundingClientRect().width + gap;
      if (step > 0) return Math.round(step); // avanza una tarjeta
    }
    return Math.round(carousel.clientWidth * 0.9) || 300;
  }

  function findCarouselFor(button) {
    var scope = button.closest('[data-napu-carousel-scope]') ||
                button.closest('section') ||
                document;
    return scope.querySelector('[data-napu-carousel]');
  }

  function initCarousels() {
    document.querySelectorAll('[data-napu-carousel-prev]').forEach(function (btn) {
      if (btn.dataset.napuBound) return;
      btn.dataset.napuBound = '1';
      btn.addEventListener('click', function () {
        var c = findCarouselFor(btn);
        if (c) c.scrollBy({ left: -carouselScrollAmount(c), behavior: 'smooth' });
      });
    });
    document.querySelectorAll('[data-napu-carousel-next]').forEach(function (btn) {
      if (btn.dataset.napuBound) return;
      btn.dataset.napuBound = '1';
      btn.addEventListener('click', function () {
        var c = findCarouselFor(btn);
        if (c) c.scrollBy({ left: carouselScrollAmount(c), behavior: 'smooth' });
      });
    });
  }

  /* ---------- 4. Parallax ---------- */
  var parallaxAbs = [];
  var parallaxRel = [];
  var parallaxTicking = false;
  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function collectParallax() {
    parallaxAbs = Array.prototype.slice.call(document.querySelectorAll('[data-parallax-abs]'));
    parallaxRel = Array.prototype.slice.call(document.querySelectorAll('[data-parallax-rel]'));
  }

  function runParallax() {
    parallaxTicking = false;
    var y = scrollY();
    var vh = window.innerHeight;
    parallaxAbs.forEach(function (el) {
      var s = parseFloat(el.dataset.parallaxAbs) || 0;
      el.style.transform = 'translate3d(0,' + (y * s).toFixed(1) + 'px,0)';
    });
    parallaxRel.forEach(function (el) {
      var s = parseFloat(el.dataset.parallaxRel) || 0;
      var r = el.getBoundingClientRect();
      var d = (vh / 2) - (r.top + r.height / 2);
      el.style.transform = 'translate3d(0,' + (d * s).toFixed(1) + 'px,0)';
    });
  }

  function requestParallax() {
    if (parallaxTicking) return;
    parallaxTicking = true;
    window.requestAnimationFrame(runParallax);
  }

  function initParallax() {
    collectParallax();
    if (!parallaxAbs.length && !parallaxRel.length) return;
    if (prefersReducedMotion) return; // respeta accesibilidad
    window.addEventListener('scroll', requestParallax, { passive: true });
    window.addEventListener('resize', requestParallax, { passive: true });
    runParallax();
  }

  /* ---------- Init ---------- */
  function init() {
    observeReveals(document);
    initNavShrink();
    initCarousels();
    initParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ---------- Shopify theme editor: re-init al añadir/editar secciones ---------- */
  document.addEventListener('shopify:section:load', function (e) {
    observeReveals(e.target);
    initNavShrink();
    initCarousels();
    collectParallax();
  });
})();

/* =============================================================
   Contador animado de las cifras del hero ([data-napu-count])

   La cifra ya viene calculada y formateada desde Liquid, asi que sin este
   bloque se ve igualmente bien: esto solo anade la animacion de conteo cuando
   el numero entra en pantalla.

   Se cuenta desde el 88% de la cifra, no desde cero: subir desde cero tarda
   demasiado en cifras de miles y da la sensacion de que la web esta contando
   ventas en directo, que no es el caso.
   ============================================================= */
(function () {
  'use strict';

  var nodes = document.querySelectorAll('[data-napu-count]');
  if (!nodes.length) return;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || !('IntersectionObserver' in window)) return;

  function formatea(n, prefijo) {
    try { return prefijo + n.toLocaleString('es-ES'); }
    catch (e) { return prefijo + n; }
  }

  function anima(el) {
    var fin = parseInt(el.getAttribute('data-napu-count'), 10);
    if (!fin || fin < 0) return;
    var prefijo = el.getAttribute('data-napu-count-prefix') || '';
    var ini = Math.floor(fin * 0.88);
    var t0 = null, dur = 1100;

    function paso(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min((ts - t0) / dur, 1);
      /* easeOutCubic: arranca rapido y frena al final, que es lo que hace que
         parezca que se detiene en una cifra y no que se corta de golpe. */
      var e = 1 - Math.pow(1 - p, 3);
      el.textContent = formatea(Math.round(ini + (fin - ini) * e), prefijo);
      if (p < 1) requestAnimationFrame(paso);
    }
    requestAnimationFrame(paso);
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      io.unobserve(entry.target);
      anima(entry.target);
    });
  }, { threshold: 0.4 });

  for (var i = 0; i < nodes.length; i++) io.observe(nodes[i]);
})();

/* =============================================================
   Ofertas de la semana: cuenta atras y revelado del relevo
   (sections/napu-ofertas-semana.liquid)

   Dos cosas, y las dos dependen de datos que pone Liquid en la seccion:

   1. La CUENTA ATRAS al proximo lunes. El instante objetivo viene ya resuelto
      desde el servidor en data-objetivo, con el desfase horario de Madrid
      incluido, asi que aqui no se calcula ninguna fecha: solo se resta. Si se
      calculara en el navegador, cada visitante veria la cuenta atras de su
      propio huso.

   2. El REVELADO. La gracia de rotar ofertas cada lunes es que se note, y a
      las 00:00 de un lunes no hay nadie mirando. Asi que el revelado no se
      dispara solo al llegar a cero: tambien la primera vez que un navegador ve
      una semana distinta a la ultima que guardo. Es decir, cuando alguien
      entra el lunes por la manana, que es cuando de verdad hay publico.
   ============================================================= */
(function () {
  'use strict';

  var secciones = document.querySelectorAll('[data-napu-ofertas]');
  if (!secciones.length) return;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var CLAVE = 'napu_ofertas_semana';

  function dos(n) { return n < 10 ? '0' + n : '' + n; }

  Array.prototype.forEach.call(secciones, function (sec) {
    var semana = sec.getAttribute('data-semana');
    if (semana) {
      /* En navegacion privada o con el almacenamiento bloqueado esto lanza.
         No es motivo para quedarse sin seccion: se pierde solo el revelado. */
      try {
        if (localStorage.getItem(CLAVE) !== semana) {
          localStorage.setItem(CLAVE, semana);
          if (!reduce) sec.classList.add('napu-of--revelar');
        }
      } catch (e) {}
    }

    var elD = sec.querySelector('[data-of-d]');
    if (!elD) return;
    var elH = sec.querySelector('[data-of-h]');
    var elM = sec.querySelector('[data-of-m]');
    var elS = sec.querySelector('[data-of-s]');

    var objetivo = Date.parse(sec.getAttribute('data-objetivo') || '');
    if (isNaN(objetivo)) return;

    /* Si la pagina llega de la cache con el objetivo ya pasado, se pintan ceros
       y no se recarga. Recargar aqui seria un bucle infinito: la pagina
       cacheada volveria con el mismo objetivo vencido. Solo se recarga cuando
       el cronometro cruza el cero teniendo a alguien delante. */
    var enMarcha = objetivo - Date.now() > 0;

    function tic() {
      var falta = objetivo - Date.now();

      if (falta <= 0) {
        elD.textContent = '0';
        elH.textContent = '00';
        elM.textContent = '00';
        elS.textContent = '00';
        if (enMarcha) {
          enMarcha = false;
          if (!reduce) sec.classList.add('napu-of--revelar');
          /* Hay que recargar: que plantas tocan esta semana lo decide Liquid en
             el servidor a partir de la etiqueta oferta-AAAA-MM-DD, y eso el
             navegador no lo puede resolver solo. La animacion tapa la recarga. */
          setTimeout(function () { location.reload(); }, reduce ? 300 : 1200);
        }
        return;
      }

      var seg = Math.floor(falta / 1000);
      elD.textContent = Math.floor(seg / 86400);
      elH.textContent = dos(Math.floor(seg / 3600) % 24);
      elM.textContent = dos(Math.floor(seg / 60) % 60);
      elS.textContent = dos(seg % 60);
      setTimeout(tic, 1000);
    }

    tic();
  });
})();

/* =============================================================
   Carrusel de la tarjeta de catalogo (snippets/napu-card-carousel.liquid)

   En movil no hace falta nada: el carril usa scroll-snap nativo y se desliza
   con el dedo. Este bloque solo anade lo que el CSS no puede:
     - marcar el puntito activo al desplazar el carril
     - en escritorio, pasar las fotos con las flechas que aparecen al hover

   Va TODO por delegacion en document, con un unico juego de listeners para
   todo el catalogo: hay mas de 100 tarjetas y poner listeners por tarjeta
   seria caro y habria que reengancharlos cada vez que la rejilla se repinta
   al filtrar. Asi funciona tambien con las tarjetas que aparecen despues.
   ============================================================= */
(function () {
  'use strict';

  function slideIndex(track) {
    return track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
  }

  function paintDots(cc, index) {
    var dots = cc.querySelectorAll('.napu-cc__dots span');
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('is-active', i === index);
    }
  }

  /* El evento scroll no burbujea: se escucha en fase de captura. */
  document.addEventListener('scroll', function (e) {
    var track = e.target;
    if (!track || !track.classList || !track.classList.contains('napu-cc__track')) return;
    var cc = track.closest('[data-napu-cc]');
    if (cc) paintDots(cc, slideIndex(track));
  }, true);

  /* Escritorio: flechas al pasar el raton, en vez de rotacion automatica.

     La rotacion automatica se descarto tras probarla, por dos razones:
     1. Se quedaba en bucle. Al avanzar el carril cambia el elemento que hay
        bajo el cursor (de una diapositiva a la siguiente), lo que dispara un
        mouseleave de la diapositiva vieja; el codigo lo lea como "el raton
        salio de la tarjeta", paraba y volvia a la primera imagen. Y vuelta a
        empezar.
     2. Aunque se arreglara, rotar cada 1,3 s no deja pararse a mirar la foto
        que interesa. Las flechas dan control y no necesitan temporizadores,
        asi que esta clase de fallo no puede volver. */
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-napu-cc-nav]');
    if (!btn) return;
    /* La tarjeta entera es un enlace al producto: sin esto, pulsar la flecha
       navegaria a la ficha en vez de pasar la foto. */
    e.preventDefault();
    e.stopPropagation();
    var cc = btn.closest('[data-napu-cc]');
    var track = cc && cc.querySelector('.napu-cc__track');
    if (!track) return;
    var total = track.children.length;
    if (total < 2) return;
    var dir = btn.getAttribute('data-napu-cc-nav') === 'next' ? 1 : -1;
    var next = (slideIndex(track) + dir + total) % total;
    track.scrollTo({ left: next * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
  });
})();

/* =============================================================
   5. Búsqueda de productos — la ÚNICA regla del tema
   -------------------------------------------------------------
   La usan el buscador de la cabecera (napu-header) y el de «otras
   secciones» del catálogo (napu-collection). Si hay que tocar cómo se
   busca, se toca aquí y valen las dos.

   POR QUÉ PRUEBA DOS VECES. /search/suggest.json de Shopify ha cambiado
   de comportamiento DOS VECES sin avisar y sin dar ningún error:

     4-sep-2026   «Sanse» -> 0    «Sanse*» -> 2   (hacía falta el asterisco)
     11-sep-2026  «Sanse» -> 2    «Sanse*» -> 0   (y ahora el asterisco rompe)

   El 4-sep se arregló añadiendo siempre el asterisco, con la idea de que
   «si algún día vuelve a funcionar sin él, no estorba». Era falso: el
   11-sep el buscador de la web decía «No encontramos nada» a quien
   escribía «Sanse» teniendo dos Sansevierias en el catálogo, y
   «ficus» pasaba de 6 resultados a 4.

   Así que no se apuesta por ninguno de los dos: se pide el término tal
   cual y, SOLO si no devuelve nada, se repite con el asterisco. Funciona
   con cualquiera de los dos comportamientos y con el que venga, y en el
   caso normal sigue siendo una única petición.

   Devuelve { productos, comodin }: comodin dice si hizo falta el asterisco,
   para que el enlace «ver todos» y el envío del formulario busquen igual
   que las sugerencias y no se contradigan con ellas.
   ============================================================= */
(function () {
  'use strict';

  function pedir(q, limite, signal) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
              '&resources[type]=product&resources[limit]=' + (limite || 6);
    return fetch(url, { signal: signal, headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        return (d && d.resources && d.resources.results && d.resources.results.products) || [];
      });
  }

  window.napuSugerencias = function (termino, limite, signal) {
    /* Si el cliente ya puso un asterisco o está buscando una frase exacta
       entre comillas, no se le añade nada: ahí un asterisco la rompería. */
    var admiteComodin = termino.indexOf('*') === -1 && termino.indexOf('"') === -1;
    return pedir(termino, limite, signal).then(function (lista) {
      if (lista.length || !admiteComodin) return { productos: lista, comodin: false };
      return pedir(termino + '*', limite, signal).then(function (conComodin) {
        return { productos: conComodin, comodin: conComodin.length > 0 };
      });
    });
  };
})();
